import type { MapViewerNode } from '@3ddv/dvm-internal';
import {
  IPopoverManager,
  IPopoverPositioner,
  POPOVER_POSITIONER,
  PopoverConfig,
  SectionPopoverComponent,
} from '@3ddv/software-division-components/dvm/popover';
import { ComponentRef, Inject, Injectable, Type } from '@angular/core';
import { createPopper } from '@popperjs/core';

/**
 * Drop-in replacement for SDC {@link PopoverManagerService} that keeps a handle to the
 * active popover component so we can refresh OnPush inputs after Best Available toggles.
 */
@Injectable()
export class GroupSalesPopoverManagerService implements IPopoverManager {
  private currentPopoverId = '';
  private tooltip?: HTMLElement;
  private activeComponentRef: ComponentRef<unknown> | null = null;

  constructor(
    @Inject(POPOVER_POSITIONER) private readonly positioner: IPopoverPositioner
  ) {}

  showPopover<T>(config: PopoverConfig<T>): void {
    const viewContainer = config.container;
    viewContainer.clear();
    this.activeComponentRef = null;
    this.currentPopoverId = config.nodeId;
    this.tooltip = config.tooltip;

    const componentRef: ComponentRef<T> = viewContainer.createComponent(
      config.component as Type<T>
    );
    this.activeComponentRef = componentRef;

    if (config.componentInputs) {
      const instanceRecord = componentRef.instance as Record<string, unknown>;
      for (const [key, value] of Object.entries(config.componentInputs)) {
        instanceRecord[key] = value;
      }
    }

    if (config.eventHandlers) {
      const instanceRecord = componentRef.instance as Record<string, unknown>;
      for (const [key, handler] of Object.entries(config.eventHandlers)) {
        const emitter = instanceRecord[key];
        const subscribeFn = (
          emitter as { subscribe?: (h: (...args: unknown[]) => void) => void }
        )?.subscribe;
        if (typeof subscribeFn === 'function') {
          subscribeFn.call(emitter, handler as (...args: unknown[]) => void);
        }
      }
    }

    const node = config.node as MapViewerNode;
    const placement = this.positioner.calculatePlacement(
      node,
      config.containerElement
    );
    const offset = this.positioner.calculateOffset(node);

    createPopper(node, this.tooltip as HTMLElement, {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [],
          },
        },
      ],
    });

    this.tooltip.setAttribute('data-show', '');
  }

  hidePopover(nodeId: string): void {
    if (this.currentPopoverId === nodeId) {
      this.tooltip?.removeAttribute('data-show');
    }
  }

  clearAll(): void {
    this.tooltip?.removeAttribute('data-show');
    this.currentPopoverId = '';
    this.activeComponentRef = null;
  }

  /**
   * Updates the mobile Select/Deselect label without closing the popover.
   * {@link SectionPopoverComponent} uses OnPush and will not re-render when nested fields mutate.
   */
  syncSectionPopoverSelection(isSelectedForBA: boolean): void {
    const componentRef = this.activeComponentRef;
    if (!componentRef) {
      return;
    }

    const instance = componentRef.instance;
    if (!(instance instanceof SectionPopoverComponent)) {
      return;
    }

    const previousData = instance.data;
    if (!previousData) {
      return;
    }

    instance.data = {
      ...previousData,
      isSelectedForBA,
    };
    componentRef.changeDetectorRef.markForCheck();
  }
}
